import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faTag, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { LibraryAttributeOption, LibraryConcept } from '../../api/centerstageApi';
import { CENTERSTAGE_COPY as COPY } from './centerstageCopy';
import type { CenterstageStyles } from './centerstageStyles';

const SUGGEST_MIN_CHARS = 2;
const SUGGEST_MAX_ROWS = 7;

export interface CenterstageFindBarProps {
  styles: CenterstageStyles;
  /** What is typed in the field; only Enter turns it into the search term. */
  draft: string;
  onDraftChange: (draft: string) => void;
  /** The committed search term. */
  searchTerm: string;
  /** Commits a search term ('' removes it). */
  onCommitSearch: (term: string) => void;
  concepts: readonly LibraryConcept[];
  categoryNames: ReadonlyMap<number, string>;
  /** Sets the concept filter and clears the search term. */
  onPickConcept: (conceptId: number) => void;
  /** Shows "or browse all concepts"; the Author with AI picker leaves it out. */
  onBrowseConcepts?: () => void;
  /** The field's accessible name and placeholder; the Centerstage page's video wording by default. */
  labels?: { searchLabel: string; searchPlaceholder: string };
  /** The type and setting selects; the Author with AI picker leaves them out. */
  types?: readonly LibraryAttributeOption[];
  settings?: readonly LibraryAttributeOption[];
  type?: string | null;
  setting?: string | null;
  onTypeChange?: (value: string | null) => void;
  onSettingChange?: (value: string | null) => void;
}

/**
 * The search field with its concept autosuggest (a combobox), the "browse
 * all concepts" link, and the type and setting selects. Typing updates the
 * draft and suggests concepts; Enter commits the draft as the search term.
 * The link and the selects render only when their handlers are given.
 */
export function CenterstageFindBar({
  styles,
  draft,
  onDraftChange,
  searchTerm,
  onCommitSearch,
  concepts,
  categoryNames,
  onPickConcept,
  onBrowseConcepts,
  labels,
  types = [],
  settings = [],
  type = null,
  setting = null,
  onTypeChange,
  onSettingChange,
}: CenterstageFindBarProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const headerId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  const matches = useMemo(() => {
    const needle = draft.trim().toLowerCase();
    if (needle.length < SUGGEST_MIN_CHARS) return [];
    return concepts.filter((concept) => concept.name.toLowerCase().includes(needle)).slice(0, SUGGEST_MAX_ROWS);
  }, [concepts, draft]);
  const expanded = open && matches.length > 0;

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  useEffect(() => {
    if (!expanded) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(event.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [expanded]);

  const pick = (concept: LibraryConcept) => {
    close();
    onDraftChange('');
    onPickConcept(concept.id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (matches.length === 0) return;
      event.preventDefault();
      setOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => {
        if (!expanded || current < 0) return step === 1 ? 0 : matches.length - 1;
        return (current + step + matches.length) % matches.length;
      });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const active = expanded && activeIndex >= 0 ? matches[activeIndex] : undefined;
      if (active) {
        pick(active);
        return;
      }
      close();
      onCommitSearch(draft.trim());
      return;
    }
    if (event.key === 'Escape' && expanded) {
      // Only the autosuggest closes; the typed text stays.
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  const clear = () => {
    close();
    onDraftChange('');
    if (searchTerm) onCommitSearch('');
    inputRef.current?.focus();
  };

  const showClear = draft.length > 0 || searchTerm.length > 0;

  return (
    <div style={styles.findBar}>
      <div ref={rowRef} style={styles.searchRow}>
        <div style={{ ...styles.searchPill, ...(focused ? styles.searchPillFocused : {}) }}>
          <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" style={{ fontSize: '14px' }} />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label={labels?.searchLabel ?? COPY.searchLabel}
            aria-expanded={expanded}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={expanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
            placeholder={labels?.searchPlaceholder ?? COPY.searchPlaceholder}
            value={draft}
            onChange={(event) => {
              onDraftChange(event.target.value);
              setOpen(true);
              setActiveIndex(-1);
            }}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={styles.searchInput}
          />
          {showClear ? (
            <button type="button" aria-label={COPY.clearSearch} onClick={clear} style={styles.clearButton}>
              <FontAwesomeIcon icon={faXmark} aria-hidden="true" style={{ fontSize: '14px' }} />
            </button>
          ) : null}
          {onBrowseConcepts ? (
            <span style={styles.browseGroup}>
              <span aria-hidden="true">{COPY.browsePrefix}</span>
              <button
                type="button"
                onClick={() => {
                  close();
                  onBrowseConcepts();
                }}
                style={styles.browseLink}
              >
                {COPY.browseConcepts}
              </button>
            </span>
          ) : null}
        </div>
        {expanded ? (
          <div style={styles.suggest}>
            <p id={headerId} style={styles.suggestHeader}>{COPY.suggestHeader}</p>
            <ul id={listId} role="listbox" aria-labelledby={headerId} style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {matches.map((concept, index) => (
                <li
                  key={concept.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  // Keep focus in the field so the pick does not blur it.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(concept)}
                  style={{ ...styles.suggestRow, ...(index === activeIndex ? styles.suggestRowActive : {}) }}
                >
                  <span aria-hidden="true" style={styles.suggestGlyph}><FontAwesomeIcon icon={faTag} /></span>
                  <span style={styles.suggestName}>{concept.name}</span>
                  {concept.categoryId !== null && categoryNames.has(concept.categoryId) ? (
                    <span style={styles.suggestCategory}>{categoryNames.get(concept.categoryId)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {onTypeChange ? (
        <AttributeSelect
          styles={styles}
          label={COPY.typeLabel}
          allLabel={COPY.allTypes}
          options={types}
          value={type}
          onChange={onTypeChange}
        />
      ) : null}
      {onSettingChange ? (
        <AttributeSelect
          styles={styles}
          label={COPY.settingLabel}
          allLabel={COPY.allSettings}
          options={settings}
          value={setting}
          onChange={onSettingChange}
        />
      ) : null}
    </div>
  );
}

interface AttributeSelectProps {
  styles: CenterstageStyles;
  label: string;
  allLabel: string;
  options: readonly LibraryAttributeOption[];
  value: string | null;
  onChange: (value: string | null) => void;
}

/**
 * A native select, hidden until the server offers options: type and setting
 * arrive with an upstream Centerstage change, and the selects appear on their
 * own once the filters read returns them.
 */
function AttributeSelect({ styles, label, allLabel, options, value, onChange }: AttributeSelectProps) {
  if (options.length === 0) return null;
  return (
    <select
      aria-label={label}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value || null)}
      style={{ ...styles.select, ...(value ? styles.selectSet : {}) }}
    >
      <option value="">{allLabel}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}
